import React, { useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, Input, List, Popconfirm, Row, Space, Spin, Typography, message } from "antd";
import { ReloadOutlined, SendOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useTranslation } from "react-i18next";
import Constants from "../../Utils/Constants";
import useToken from "../../Authentication/useToken";

const { Title, Text } = Typography;
const { TextArea } = Input;

const formatDateTime = (value, locale) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString(locale);
};

const getSenderLabel = (senderName, senderType, t) => {
  if (senderName) return senderName;

  const senderLabels = {
    kinesiologist: t("Kinesiologist.Messaging.sender_kinesiologist"),
    patient: t("Kinesiologist.Messaging.sender_patient"),
    caregiver: t("Kinesiologist.Messaging.sender_caregiver"),
  };

  return senderLabels[senderType] || senderType;
};

const shouldRetry = (failureCount, error) => {
  const status = error?.response?.status;
  // Authentication/authorization errors should stop immediately instead of looping.
  if (status === 401 || status === 403) return false;
  return failureCount < 2;
};

const getRefetchInterval = (query, intervalMs) => {
  const status = query?.state?.error?.response?.status;
  // Disable polling once the session is no longer valid.
  if (status === 401 || status === 403) return false;
  return intervalMs;
};

export default function KinesiologistMessaging() {
  const { t, i18n } = useTranslation("Professionals");
  const { token } = useToken();
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [sending, setSending] = useState(false);
  const [manualRefreshing, setManualRefreshing] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editingSubject, setEditingSubject] = useState("");
  const [editingContent, setEditingContent] = useState("");
  const [messageActionLoading, setMessageActionLoading] = useState(false);

  const authHeaders = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  // Charge la liste des conversations avec le dernier message + compteur non lu.
  const {
    data: conversations = [],
    isLoading: loadingConversations,
    error: conversationsError,
    refetch: refetchConversations,
  } = useQuery(["kinesiologist-messaging-conversations"], async () => {
    const response = await axios.get(
      `${Constants.SERVER_URL}/messages/kinesiologist/conversations`,
      authHeaders
    );
    const data = response.data?.data || [];
    console.log("📨 Conversations reçues avec unreadCount:", data);
    return data;
  }, {
    enabled: !!token,
    staleTime: 2000,
    cacheTime: 5000,
    retry: shouldRetry,
    refetchInterval: (_, query) => (token ? getRefetchInterval(query, 5000) : false),
  });

  const selectedPatientId = selectedConversation?.PatientId;

  // Charge le fil de discussion pour le patient actuellement sélectionné.
  const {
    data: messages = [],
    isLoading: loadingMessages,
    error: messagesError,
    refetch: refetchMessages,
  } = useQuery(
    ["kinesiologist-messaging-thread", selectedPatientId],
    async () => {
      if (!selectedPatientId) return [];
      const response = await axios.get(
        `${Constants.SERVER_URL}/messages/kinesiologist/conversation/${selectedPatientId}`,
        authHeaders
      );
      return response.data?.data || [];
    },
    {
      enabled: !!selectedPatientId && !!token,
      staleTime: 1000,
      cacheTime: 5000,
      retry: shouldRetry,
      refetchInterval: (_, query) => (token && selectedPatientId ? getRefetchInterval(query, 3000) : false),
    }
  );

  const handleSelectConversation = (conversation) => {
    setSelectedConversation(conversation);
    setSubject(conversation?.subject || "");
    setContent("");
  };

  const handleSend = async () => {
    if (!selectedPatientId || !content.trim()) return;

    setSending(true);
    try {
      const response = await axios.post(
        `${Constants.SERVER_URL}/messages/kinesiologist/reply`,
        {
          patientId: Number(selectedPatientId),
          subject: (subject || t("Kinesiologist.Messaging.default_subject")).trim(),
          content: content.trim(),
        },
        authHeaders
      );

      const isSuccess = response?.data?.success !== false;
      if (!isSuccess) {
        const backendMessage = response?.data?.message;
        message.error(backendMessage || t("Kinesiologist.Messaging.error_send"));
        return;
      }

      setContent("");
      message.success(t("Kinesiologist.Messaging.success_send"));
      await Promise.all([refetchMessages(), refetchConversations()]);
    } catch (error) {
      console.error("Erreur d'envoi du message kinésiologue:", error);
      const backendMessage = error?.response?.data?.message;
      message.error(backendMessage || t("Kinesiologist.Messaging.error_send"));
    } finally {
      setSending(false);
    }
  };

  // Démarre l'édition inline d'un message sortant du kinésiologue.
  const handleStartEdit = (msg) => {
    setEditingMessageId(msg.id);
    setEditingSubject(msg.subject || "");
    setEditingContent(msg.content || "");
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditingSubject("");
    setEditingContent("");
  };

  const handleSaveEdit = async () => {
    if (!editingMessageId || !editingContent.trim()) return;

    setMessageActionLoading(true);
    try {
      await axios.put(
        `${Constants.SERVER_URL}/messages/kinesiologist/message/${editingMessageId}`,
        {
          subject: editingSubject.trim() || t("Kinesiologist.Messaging.default_subject"),
          content: editingContent.trim(),
        },
        authHeaders
      );

      message.success(t("Kinesiologist.Messaging.success_update"));
      handleCancelEdit();
      await Promise.all([refetchMessages(), refetchConversations()]);
    } catch (error) {
      const backendMessage = error?.response?.data?.message;
      message.error(backendMessage || t("Kinesiologist.Messaging.error_update"));
    } finally {
      setMessageActionLoading(false);
    }
  };

  // Suppression logique du message (suppression "de ma vue").
  const handleDeleteMessage = async (messageId) => {
    setMessageActionLoading(true);
    try {
      await axios.delete(
        `${Constants.SERVER_URL}/messages/kinesiologist/message/${messageId}`,
        authHeaders
      );

      message.success(t("Kinesiologist.Messaging.success_delete"));
      if (editingMessageId === messageId) handleCancelEdit();
      await Promise.all([refetchMessages(), refetchConversations()]);
    } catch (error) {
      const backendMessage = error?.response?.data?.message;
      message.error(backendMessage || t("Kinesiologist.Messaging.error_delete"));
    } finally {
      setMessageActionLoading(false);
    }
  };

  const handleRefresh = async () => {
    setManualRefreshing(true);
    try {
      const tasks = [refetchConversations()];
      if (selectedPatientId) tasks.push(refetchMessages());
      await Promise.all(tasks);
      message.success(t("Kinesiologist.Messaging.success_refresh"));
    } catch (error) {
      message.error(t("Kinesiologist.Messaging.error_refresh"));
    } finally {
      setManualRefreshing(false);
    }
  };

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={3} style={{ margin: 0 }}>
            {t("Kinesiologist.Messaging.title")}
          </Title>
        </Col>
        <Col>
          <Button icon={<ReloadOutlined spin={manualRefreshing} />} onClick={handleRefresh} disabled={manualRefreshing}>
            {t("Kinesiologist.Messaging.button_refresh")}
          </Button>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Card title={t("Kinesiologist.Messaging.conversations_title")} styles={{ body: { padding: 0 } }}>
            {loadingConversations ? (
              <div style={{ padding: 16, textAlign: "center" }}><Spin /></div>
            ) : conversationsError ? (
              <Alert type="error" message={t("Kinesiologist.Messaging.error_loading_conversations")} showIcon style={{ margin: 12 }} />
            ) : (
              <List
                dataSource={conversations}
                locale={{ emptyText: t("Kinesiologist.Messaging.empty_conversations") }}
                renderItem={(item) => {
                  const isActive = selectedPatientId === item.PatientId;
                  // unreadCount vient du backend. Le fallback garde un badge style WhatsApp
                  // quand le dernier message provient d'un patient/caregiver.
                  const unreadCountValue = Number(item.unreadCount ?? 0);
                  const unreadCount = Number.isFinite(unreadCountValue) ? unreadCountValue : 0;
                  const fallbackUnread = item.senderType !== "kinesiologist" ? 1 : 0;
                  const displayUnread = unreadCount > 0 ? unreadCount : fallbackUnread;
                  
                  return (
                    <List.Item
                      key={`conv-${item.PatientId}`}
                      onClick={() => handleSelectConversation(item)}
                      style={{
                        cursor: "pointer",
                        padding: "12px 16px",
                        background: isActive ? "#e6f4ff" : "transparent",
                      }}
                    >
                      <div style={{ width: "100%", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <Space direction="vertical" size={2} style={{ width: "100%" }}>
                            <Text strong ellipsis={{ tooltip: true }} style={{ display: "block", maxWidth: "100%" }}>
                              {item.patientFirstname} {item.patientLastname}
                            </Text>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {t("Kinesiologist.Messaging.last_message", {
                                sender: getSenderLabel(item.senderName, item.senderType, t),
                                date: formatDateTime(item.sentAt, i18n.language),
                              })}
                            </Text>
                            <Text ellipsis={{ tooltip: true }} style={{ display: "block", maxWidth: "100%" }}>
                              {item.subject || t("Kinesiologist.Messaging.no_subject")}
                            </Text>
                          </Space>
                        </div>
                        {displayUnread > 0 && (
                          <Badge 
                            count={displayUnread}
                            overflowCount={99}
                            color="#25D366"
                            style={{
                              backgroundColor: "#25D366",
                              color: "#ffffff",
                              minWidth: 22,
                              height: 22,
                              fontSize: 11,
                              lineHeight: "22px",
                              fontWeight: 700,
                              boxShadow: "0 0 0 2px #fff",
                            }}
                          />
                        )}
                      </div>
                    </List.Item>
                  );
                }}
              />
            )}
          </Card>
        </Col>

        <Col span={16}>
          <Card title={selectedPatientId ? t("Kinesiologist.Messaging.thread_title", { patientId: selectedPatientId }) : t("Kinesiologist.Messaging.select_conversation_title")}>
            {!selectedPatientId ? (
              <Alert type="info" message={t("Kinesiologist.Messaging.select_conversation_help")} showIcon />
            ) : (
              <>
                {loadingMessages ? (
                  <div style={{ textAlign: "center", padding: 24 }}><Spin /></div>
                ) : messagesError ? (
                  <Alert type="error" message={t("Kinesiologist.Messaging.error_loading_history")} showIcon />
                ) : (
                  <List
                    dataSource={messages}
                    locale={{ emptyText: t("Kinesiologist.Messaging.empty_messages") }}
                    style={{ marginBottom: 16, maxHeight: 360, overflowY: "auto" }}
                    renderItem={(msg) => {
                      const isOutgoing = msg.senderType === "kinesiologist";
                      const isEditing = editingMessageId === msg.id;
                      return (
                        <List.Item key={`msg-${msg.id}`} style={{ borderBottom: "none", padding: "8px 0" }}>
                          <div
                            style={{
                              width: "100%",
                              display: "flex",
                              justifyContent: isOutgoing ? "flex-end" : "flex-start",
                            }}
                          >
                            <div
                              style={{
                                maxWidth: "80%",
                                width: "fit-content",
                                background: isOutgoing ? "#e6f4ff" : "#f5f5f5",
                                border: `1px solid ${isOutgoing ? "#91caff" : "#d9d9d9"}`,
                                borderRadius: 12,
                                padding: "10px 12px",
                              }}
                            >
                              {isEditing ? (
                                <Space direction="vertical" size={8} style={{ width: "100%" }}>
                                  <Input
                                    value={editingSubject}
                                    onChange={(event) => setEditingSubject(event.target.value)}
                                    maxLength={255}
                                  />
                                  <TextArea
                                    rows={3}
                                    value={editingContent}
                                    onChange={(event) => setEditingContent(event.target.value)}
                                  />
                                  <Space style={{ justifyContent: "flex-end", width: "100%" }}>
                                    <Button size="small" onClick={handleCancelEdit}>
                                      {t("Kinesiologist.Messaging.button_cancel")}
                                    </Button>
                                    <Button
                                      type="primary"
                                      size="small"
                                      onClick={handleSaveEdit}
                                      loading={messageActionLoading}
                                      disabled={!editingContent.trim()}
                                    >
                                      {t("Kinesiologist.Messaging.button_save")}
                                    </Button>
                                  </Space>
                                </Space>
                              ) : (
                                <Space direction="vertical" size={2} style={{ width: "100%" }}>
                                  <Text strong>
                                    {getSenderLabel(msg.senderName, msg.senderType, t)}
                                  </Text>
                                  <Text type="secondary" style={{ fontSize: 12 }}>
                                    {formatDateTime(msg.sentAt, i18n.language)}
                                  </Text>
                                  <Text strong style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
                                    {msg.subject || t("Kinesiologist.Messaging.no_subject")}
                                  </Text>
                                  <Text style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
                                    {msg.content}
                                  </Text>
                                  <Space size={12} style={{ marginTop: 4 }}>
                                    {isOutgoing && (
                                      <Button type="link" size="small" style={{ padding: 0 }} onClick={() => handleStartEdit(msg)}>
                                        {t("Kinesiologist.Messaging.button_edit")}
                                      </Button>
                                    )}
                                    <Popconfirm
                                      title={t("Kinesiologist.Messaging.confirm_delete_title")}
                                      okText={t("Kinesiologist.Messaging.button_delete")}
                                      cancelText={t("Kinesiologist.Messaging.button_cancel")}
                                      onConfirm={() => handleDeleteMessage(msg.id)}
                                    >
                                      <Button type="link" danger size="small" style={{ padding: 0 }} loading={messageActionLoading}>
                                        {t("Kinesiologist.Messaging.button_delete")}
                                      </Button>
                                    </Popconfirm>
                                  </Space>
                                </Space>
                              )}
                            </div>
                          </div>
                        </List.Item>
                      );
                    }}
                  />
                )}

                <Space direction="vertical" style={{ width: "100%" }} size={8}>
                  <Input
                    placeholder={t("Kinesiologist.Messaging.placeholder_subject")}
                    value={subject}
                    onChange={(event) => setSubject(event.target.value)}
                    maxLength={255}
                  />
                  <TextArea
                    rows={4}
                    placeholder={t("Kinesiologist.Messaging.placeholder_message")}
                    value={content}
                    onChange={(event) => setContent(event.target.value)}
                  />
                  <div style={{ textAlign: "right" }}>
                    <Button
                      type="primary"
                      icon={<SendOutlined />}
                      onClick={handleSend}
                      loading={sending}
                      disabled={!content.trim()}
                    >
                      {t("Kinesiologist.Messaging.button_send")}
                    </Button>
                  </div>
                </Space>
              </>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
}
